// src/Pages/CreateJob.jsx
import React, {
  useState, useEffect, useCallback, useRef, useMemo
} from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Components/Sidebar';
import '../Style/CreateJob.css';
import {
  FaTrashAlt, FaCloudUploadAlt, FaSpinner, FaTimes
} from 'react-icons/fa';
import { auth, db, storage } from '../firebaseConfig';
import {
  collection, query, where, getDocs, addDoc,
  serverTimestamp, doc, getDoc
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/* ───────── helpers ───────── */
const debounce = (fn, wait) => {
  let t;
  return (...a) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...a), wait);
  };
};

const fileKind = (name, type) => {
  if (/pdf$/i.test(name) || type === 'application/pdf') return 'pdf';
  if (/csv$/i.test(name) || type === 'text/csv') return 'csv';
  if (/image\//.test(type)) return 'image';
  return 'other';
};

const MAX_FILES = 10;
const MAX_SIZE_MB = 10;

/* ───────── component ───────── */
export default function CreateJob() {
  /* core state */
  const [jobId, setJobId] = useState('Loading…');      // editable
  const [jobName, setJobName] = useState('');
  const [rfq, setRfq] = useState('');
  const [rfqs, setRfqs] = useState([]);                // all RFQs (any user, any status)
  const [jobStatus, setStatus] = useState('Pending');
  const [generalDesc, setDesc] = useState('');
  const [customer, setCustomer] = useState('');
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  /* tasks / expenses */
  const [tasks, setTasks] = useState([{ name: '', description: '', date: '', hours: '' }]);
  const [expenses, setExpenses] = useState([{
    supplierId: '', supplierName: '',
    description: '', qty: '', cost: '', markup: '', base: 0, total: 0
  }]);

  /* uploads */
  const [files, setFiles] = useState([]);
  const [fileMeta, setFileMeta] = useState([]);
  const [dragActive, setDrag] = useState(false);

  /* ui */
  const [showModal, setShow] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  /* validation */
  const [errors, setErrors] = useState({
    jobName: false,
    rfq: false,
    customer: false,
    tasks: [],
    expenses: []
  });

  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  /* ───────── totals ───────── */
  const totals = useMemo(() => {
    const costBefore = expenses.reduce(
      (s, e) => s + ((parseFloat(e.qty) || 0) * (parseFloat(e.cost) || 0)), 0
    );
    const afterMarkup = expenses.reduce((s, e) => s + (e.total || 0), 0);
    return {
      costBefore: costBefore.toFixed(2),
      afterMarkup: afterMarkup.toFixed(2)
    };
  }, [expenses]);

  /* ───────── initial load ───────── */
  useEffect(() => {
    const load = async user => {
      if (!user) {
        toast.error('Please login to create a job.', { className: 'toast-error' });
        setLoading(false);
        return;
      }
      try {
        setLoading(true);

        // suggested Job ID for this user
        const jobSnap = await getDocs(query(collection(db, 'Jobs'), where('authId', '==', user.uid)));
        setJobId(`NYA${jobSnap.size + 1}`);

        // customers
        const custSnap = await getDocs(collection(db, 'Customers'));
        setCustomers(custSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // suppliers
        const suppSnap = await getDocs(collection(db, 'Suppliers'));
        setSuppliers(suppSnap.docs.map(d => ({ id: d.id, ...d.data() })));

        // all RFQs from any user
        const rfqSnap = await getDocs(collection(db, 'RFQs'));
        setRfqs(rfqSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (err) {
        console.error(err);
        toast.error('Failed to fetch initial data.', { className: 'toast-error' });
      } finally {
        setLoading(false);
      }
    };
    const unsub = auth.onAuthStateChanged(load);
    return () => unsub();
  }, []);

  /* ───────── validation ───────── */
  const validate = useCallback(() => {
    const taskErr = tasks.map(t => !t.name.trim());
    const expErr  = expenses.map(e => !e.supplierId);
    const newErrors = {
      jobName: !jobName.trim(),
      rfq: !rfq,
      customer: !customer,
      tasks: taskErr,
      expenses: expErr
    };
    setErrors(newErrors);
    return !newErrors.jobName &&
           !newErrors.rfq &&
           !newErrors.customer &&
           !taskErr.some(Boolean) &&
           !expErr.some(Boolean);
  }, [jobName, rfq, customer, tasks, expenses]);

  /* ───────── debounced inputs ───────── */
  const handleJobNameInput = useCallback(debounce(v => setJobName(v), 300), []);
  const handleDescInput    = useCallback(debounce(v => setDesc(v), 300), []);

  /* ───────── task helpers ───────── */
  const updateTask = (idx, field, value) => {
    setTasks(t => t.map((row, i) => i === idx ? { ...row, [field]: value } : row));
    if (field === 'name') {
      setErrors(e => ({
        ...e,
        tasks: e.tasks.map((err, i) => i === idx ? !value.trim() : err)
      }));
    }
  };
  const addTask    = () => setTasks(t => [...t, { name: '', description: '', date: '', hours: '' }]);
  const removeTask = i => setTasks(t => t.filter((_, idx) => idx !== i));

  /* ───────── expense helpers ───────── */
  const recalcExpense = ex => {
    const qty    = parseFloat(ex.qty)    || 0;
    const cost   = parseFloat(ex.cost)   || 0;
    const markup = parseFloat(ex.markup) || 0;
    const base   = qty * cost;
    const total  = base * (1 + markup / 100);
    return { ...ex, base: Number(base.toFixed(2)), total: Number(total.toFixed(2)) };
  };
  const updateExpense = (idx, field, value) => {
    setExpenses(p => p.map((ex, i) => {
      if (i !== idx) return ex;
      let next = { ...ex };
      if (field === 'supplierId') {
        const sup = suppliers.find(s => s.id === value);
        next.supplierId = value;
        next.supplierName = sup ? sup.name : '';
      } else {
        next[field] = value;
      }
      return recalcExpense(next);
    }));
    if (field === 'supplierId') {
      setErrors(e => ({
        ...e,
        expenses: e.expenses.map((er, i) => i === idx ? !value : er)
      }));
    }
  };
  const addExpense    = () => setExpenses(e => [...e, {
    supplierId: '', supplierName: '',
    description: '', qty: '', cost: '', markup: '', base: 0, total: 0
  }]);
  const removeExpense = idx => setExpenses(e => e.filter((_, i) => i !== idx));

  /* ───────── file uploads ───────── */
  const addFiles = newFiles => {
    if (!newFiles.length) return;
    let existing = [...files];
    let meta     = [...fileMeta];

    for (const f of newFiles) {
      if (existing.length >= MAX_FILES) {
        toast.error(`Max ${MAX_FILES} files reached.`, { className: 'toast-error' });
        break;
      }
      if (f.size > MAX_SIZE_MB * 1024 * 1024) {
        toast.error(`${f.name} exceeds ${MAX_SIZE_MB} MB.`, { className: 'toast-error' });
        continue;
      }
      const kind = fileKind(f.name, f.type);
      existing.push(f);
      meta.push({
        name: f.name,
        kind,
        preview: kind === 'image' ? URL.createObjectURL(f) : null
      });
    }
    setFiles(existing);
    setFileMeta(meta);
  };
  const handleInputFiles = e => addFiles(Array.from(e.target.files || []));
  const onDragOver  = e => { e.preventDefault(); setDrag(true); };
  const onDragLeave = () => setDrag(false);
  const onDrop      = e => { e.preventDefault(); setDrag(false); addFiles(Array.from(e.dataTransfer.files || [])); };
  const openPicker  = () => fileInputRef.current?.click();
  const removeFile  = idx => {
    setFiles(f => f.filter((_, i) => i !== idx));
    setFileMeta(m => {
      const tgt = m[idx];
      if (tgt?.preview) URL.revokeObjectURL(tgt.preview);
      return m.filter((_, i) => i !== idx);
    });
  };
  useEffect(() => () => fileMeta.forEach(m => m.preview && URL.revokeObjectURL(m.preview)), [fileMeta]);

  /* upload to Firebase Storage */
  const uploadAll = async () => {
    if (!files.length) return { fileMeta: [], fileUrls: [] };
    try {
      const uploaded = await Promise.all(
        files.map(async f => {
          const storageRef = ref(storage, `Jobs/${jobId}/${Date.now()}_${f.name}`);
          await uploadBytes(storageRef, f);
          const url = await getDownloadURL(storageRef);
          return { name: f.name, url, kind: fileKind(f.name, f.type) };
        })
      );
      return { fileMeta: uploaded, fileUrls: uploaded.map(u => u.url) };
    } catch (err) {
      toast.error('File upload failed.', { className: 'toast-error' });
      throw err;
    }
  };

  /* ───────── save job ───────── */
  const saveJob = async e => {
    e.preventDefault();
    if (!validate()) {
      toast.error('Please correct highlighted fields.', { className: 'toast-error' });
      return;
    }
    const user = auth.currentUser;
    if (!user) {
      toast.error('Please login first.', { className: 'toast-error' });
      return;
    }
    try {
      setSaving(true);

      const uSnap = await getDoc(doc(db, 'users', user.uid));
      const { firstName = '', lastName = '' } = uSnap.exists() ? uSnap.data() : {};

      const uploaded = await uploadAll();

      const rfqObj = rfqs.find(r => r.id === rfq);
      await addDoc(collection(db, 'Jobs'), {
        jobId: jobId.trim(),
        jobName: jobName.trim(),
        linkedRfqId: rfq,
        linkedRfqNo: rfqObj?.rfqNo || rfqObj?.rfqId || '',
        status: jobStatus,
        customer,
        description: generalDesc.trim(),
        tasks: tasks.map(t => ({
          name: t.name.trim(),
          description: t.description.trim() || '—',
          date: t.date || '—',
          hours: t.hours || '—'
        })),
        expenses: expenses.map(e => ({
          supplierId: e.supplierId,
          supplierName: e.supplierName,
          description: e.description.trim() || '—',
          qty: e.qty || '0',
          cost: e.cost || '0',
          markup: e.markup || '0',
          base: e.base,
          total: e.total
        })),
        totalBeforeMarkup: Number(totals.costBefore),
        totalAfterMarkup:  Number(totals.afterMarkup),
        fileMeta: uploaded.fileMeta,
        fileUrls: uploaded.fileUrls,
        authId: user.uid,
        createdBy: `${firstName} ${lastName}`.trim(),
        createdAt: serverTimestamp()
      });

      toast.success('Job saved successfully!', { className: 'toast-success' });
      setShow(true);
      clearForm();
      setJobId(prev => {
        const n = parseInt((prev || 'NYA0').replace(/\D/g, ''), 10) + 1;
        return `NYA${n}`;
      });
    } catch (err) {
      console.error(err);
      toast.error('Could not save job.', { className: 'toast-error' });
    } finally {
      setSaving(false);
    }
  };

  /* ───────── clear form ───────── */
  const clearForm = () => {
    setJobName(''); setRfq(''); setStatus('Pending'); setDesc('');
    setCustomer('');
    setTasks([{ name: '', description: '', date: '', hours: '' }]);
    setExpenses([{
      supplierId: '', supplierName: '',
      description: '', qty: '', cost: '', markup: '', base: 0, total: 0
    }]);
    fileMeta.forEach(m => m.preview && URL.revokeObjectURL(m.preview));
    setFiles([]); setFileMeta([]);
    setErrors({ jobName: false, rfq: false, customer: false, tasks: [], expenses: [] });
  };

  /* modal navigation */
  const closeM = () => setShow(false);
  const goFail = () => navigate('/FailureReport');
  const goTest = () => navigate('/TestReport');
  const goAll  = () => navigate('/Jobs');

  useEffect(() => {
    const h = e => { if (e.key === 'Escape') setShow(false); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, []);

  /* ───────── render ───────── */
  return (
    <div className="createjob-page">
      <ToastContainer position="top-right" autoClose={2500} />

      <Sidebar />

      <main className="createjob-main">
        <div className="title-row">
          <h2>Create Job</h2>
          <span className="badge-id">{jobId}</span>
        </div>

        {loading ? (
          <div className="loading-spinner">
            <FaSpinner className="spin" size={54} />
            <p>Loading prerequisite data…</p>
          </div>
        ) : (
          <form className="createjob-form" onSubmit={saveJob}>
            {/* ───────── Job Details ───────── */}
            <section className="card">
              <h3>Job Details</h3>
              <div className="grid-5">
                <div className="field">
                  <label>Job ID</label>
                  <input
                    value={jobId}
                    onChange={e => setJobId(e.target.value)}
                    placeholder="Enter job ID"
                  />
                </div>

                <div className="field">
                  <label>Job Name <span className="required">*</span></label>
                  <input
                    placeholder="Enter job name"
                    defaultValue={jobName}
                    onChange={e => handleJobNameInput(e.target.value)}
                    className={errors.jobName ? 'input-error' : ''}
                  />
                  {errors.jobName && <span className="error-text">Required</span>}
                </div>

                <div className="field">
                  <label>RFQ <span className="required">*</span></label>
                  <select
                    value={rfq}
                    onChange={e => setRfq(e.target.value)}
                    className={errors.rfq ? 'input-error' : ''}
                  >
                    <option value="">Select RFQ</option>
                    {rfqs.map(r => (
                      <option key={r.id} value={r.id}>
                        {(r.rfqNo || r.rfqId || r.id)} {r.status ? `- ${r.status}` : ''}
                      </option>
                    ))}
                  </select>
                  {errors.rfq && <span className="error-text">Required</span>}
                </div>

                <div className="field">
                  <label>Status</label>
                  <select value={jobStatus} onChange={e => setStatus(e.target.value)}>
                    <option>Pending</option>
                    <option>In Progress</option>
                    <option>Completed</option>
                    <option>On Hold</option>
                    <option>Cancelled</option>
                  </select>
                </div>

                <div className="field">
                  <label>Customer <span className="required">*</span></label>
                  <select
                    value={customer}
                    onChange={e => setCustomer(e.target.value)}
                    className={errors.customer ? 'input-error' : ''}
                  >
                    <option value="">Select customer</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.name}>{c.name}</option>
                    ))}
                  </select>
                  {errors.customer && <span className="error-text">Required</span>}
                </div>
              </div>

              <div className="field mt">
                <label>General Description</label>
                <textarea
                  rows={3}
                  defaultValue={generalDesc}
                  placeholder="Describe the job"
                  onChange={e => handleDescInput(e.target.value)}
                />
              </div>
            </section>

            {/* ───────── Tasks ───────── */}
            <section className="card">
              <div className="sec-head">
                <h3>Tasks</h3>
                <button type="button" className="btn-mini dark" onClick={addTask}>+ Add Task</button>
              </div>
              <div className="tasks-header">
                <div>Task Name <span className="required">*</span></div>
                <div>Description</div>
                <div>Start Date</div>
                <div>Hours</div>
                <div></div>
              </div>
              {tasks.map((t, i) => (
                <div key={i} className="task-row">
                  <div className="cell">
                    <input
                      placeholder="Name"
                      value={t.name}
                      onChange={e => updateTask(i, 'name', e.target.value)}
                      className={errors.tasks[i] ? 'input-error' : ''}
                    />
                    {errors.tasks[i] && <span className="error-text mini">Required</span>}
                  </div>
                  <div className="cell">
                    <input
                      placeholder="Description"
                      value={t.description}
                      onChange={e => updateTask(i, 'description', e.target.value)}
                    />
                  </div>
                  <div className="cell">
                    <input
                      type="date"
                      value={t.date}
                      onChange={e => updateTask(i, 'date', e.target.value)}
                    />
                  </div>
                  <div className="cell">
                    <input
                      type="number"
                      min="0"
                      placeholder="Hrs"
                      value={t.hours}
                      onChange={e => updateTask(i, 'hours', e.target.value)}
                    />
                  </div>
                  <div className="cell actions">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => removeTask(i)}
                      aria-label="Remove task"
                      disabled={tasks.length === 1}
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              ))}
            </section>

            {/* ───────── Expenses ───────── */}
            <section className="card">
              <div className="sec-head">
                <h3>Expenses</h3>
                <button type="button" className="btn-mini dark" onClick={addExpense}>+ Add Expense</button>
              </div>
              <div className="expenses-header">
                <div>Supplier <span className="required">*</span></div>
                <div>Description</div>
                <div>Qty</div>
                <div>Cost</div>
                <div>Markup %</div>
                <div>Base</div>
                <div>Total</div>
                <div></div>
              </div>
              {expenses.map((ex, i) => (
                <div key={i} className="expense-row">
                  <div className="cell">
                    <select
                      value={ex.supplierId}
                      onChange={e => updateExpense(i, 'supplierId', e.target.value)}
                      className={errors.expenses[i] ? 'input-error' : ''}
                    >
                      <option value="">Select</option>
                      {suppliers.map(s => (
                        <option key={s.id} value={s.id}>{s.name}</option>
                      ))}
                    </select>
                    {errors.expenses[i] && <span className="error-text mini">Required</span>}
                  </div>
                  <div className="cell">
                    <input
                      placeholder="Description"
                      value={ex.description}
                      onChange={e => updateExpense(i, 'description', e.target.value)}
                    />
                  </div>
                  <div className="cell sm">
                    <input
                      type="number"
                      min="0"
                      placeholder="Qty"
                      value={ex.qty}
                      onChange={e => updateExpense(i, 'qty', e.target.value)}
                    />
                  </div>
                  <div className="cell sm">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Cost"
                      value={ex.cost}
                      onChange={e => updateExpense(i, 'cost', e.target.value)}
                    />
                  </div>
                  <div className="cell sm">
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="%"
                      value={ex.markup}
                      onChange={e => updateExpense(i, 'markup', e.target.value)}
                    />
                  </div>
                  <div className="cell sm readonly">
                    <input readOnly value={ex.base} title="Base cost (qty * cost)" />
                  </div>
                  <div className="cell sm readonly">
                    <input readOnly value={ex.total} title="Total after markup" />
                  </div>
                  <div className="cell actions">
                    <button
                      type="button"
                      className="icon-btn"
                      onClick={() => removeExpense(i)}
                      disabled={expenses.length === 1}
                      aria-label="Remove expense"
                    >
                      <FaTrashAlt />
                    </button>
                  </div>
                </div>
              ))}

              <div className="totals-bar">
                <div className="tot-item">
                  <span className="lbl">Total Before Markup:</span>
                  <span className="val">R{totals.costBefore}</span>
                </div>
                <div className="tot-item">
                  <span className="lbl">Total After Markup:</span>
                  <span className="val strong">R{totals.afterMarkup}</span>
                </div>
              </div>
            </section>

            {/* ───────── Attachments ───────── */}
            <section className="card">
              <h3>Attachments</h3>
              <p className="hint">
                Upload images / PDF / CSV (max {MAX_FILES} files, {MAX_SIZE_MB} MB each).
              </p>
              <div
                className={`upload-area ${dragActive ? 'drag' : ''}`}
                onClick={openPicker}
                onDragOver={onDragOver}
                onDragLeave={onDragLeave}
                onDrop={onDrop}
                role="button"
                tabIndex={0}
              >
                <FaCloudUploadAlt size={34} />
                <p>{dragActive ? 'Drop to upload' : 'Click or drag files here'}</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,application/pdf,text/csv,.csv"
                  multiple
                  hidden
                  onChange={handleInputFiles}
                />
              </div>

              {fileMeta.length > 0 && (
                <div className="files-grid">
                  {fileMeta.map((f, i) => (
                    <div key={i} className="file-chip">
                      <div className={`preview ${f.kind}`}>
                        {f.kind === 'image' ? (
                          <img src={f.preview} alt={f.name} />
                        ) : (
                          <span className="badge-kind">{f.kind.toUpperCase()}</span>
                        )}
                      </div>
                      <div className="fname" title={f.name}>{f.name}</div>
                      <button
                        type="button"
                        className="remove-chip"
                        onClick={() => removeFile(i)}
                        aria-label={`Remove ${f.name}`}
                      >
                        <FaTimes />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ───────── Actions ───────── */}
            <section className="card action-card">
              <div className="actions-row">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  {saving ? 'Saving…' : 'Save Job'}
                </button>
                <button
                  type="button"
                  className="btn-outline"
                  onClick={clearForm}
                  disabled={saving}
                >
                  Clear Form
                </button>
              </div>
            </section>
          </form>
        )}
      </main>

      {/* ───────── overlays ───────── */}
      {saving && (
        <div className="saving-overlay">
          <FaSpinner className="spin" size={58} />
          <p>Saving…</p>
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" role="dialog" aria-labelledby="modal-title">
          <div className="modal-card">
            <button className="modal-close" onClick={closeM} aria-label="Close modal">×</button>
            <h3 id="modal-title">Job Created</h3>
            <p>Select an action below:</p>
            <div className="modal-actions">
              <button onClick={closeM}>Create Another Job</button>
              <button onClick={goFail}>Go to Failure Report</button>
              <button onClick={goTest}>Go to Test Report</button>
              <button onClick={goAll}>View All Jobs</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
